import { ActionIcon, Badge, Group, Paper, Text, Tooltip } from "@mantine/core";
import moment from 'moment';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, Views, dayjsLocalizer } from 'react-big-calendar';
import { useNavigate } from "react-router-dom";
import { groupBy, extenseWeekday, getExtenseDatetime } from "../utility/util";
import { FaCheck } from "react-icons/fa6";
import { GET_ALL_SUPPORTS } from "../services/supports";
import { GET_ALL_CONTRACTS } from "../services/contracts";
import { GET_ALL_CUSTOMERS } from "../services/customers";
import { showNotification } from "@mantine/notifications";
dayjs.locale('pt-br')

const localizer = dayjsLocalizer(dayjs)

function Event({ event }: any) {
    return <div style={{ display: 'flex', flexDirection: 'row', whiteSpace: 'nowrap', alignItems: 'center' }}>
        {event.done && <ActionIcon mr="3px" size="sm" color={"green"} variant={"filled"}><FaCheck /></ActionIcon>}
        <Text c="black" fw="bold" size="xs">{event.title}</Text>
    </div>
}

export default function CalendarPage() {
    const [loadingData, setLoadingData] = useState<boolean>(false);
    const [tasks, setTasks] = useState<any[]>([]);
    const [customers, setCustomers] = useState<any[]>([]);
    const [supports, setSupports] = useState<any[]>([]);
    const [contracts, setContracts] = useState<any[]>([]);
    const navigate = useNavigate();

    const loadData = async () => {
        try {
            setLoadingData(true);
            setContracts(await GET_ALL_CONTRACTS());
            setSupports(await GET_ALL_SUPPORTS());
            setCustomers(await GET_ALL_CUSTOMERS());
        } catch (err: any) {
            showNotification({ message: err.message, color: "red" })
        } finally {
            setLoadingData(false);
        }
    }

    const { messages, formats, components, defaultDate } = useMemo(
        () => ({
            components: {
                // agenda: {
                //     event: Event,
                // },
                month: { event: Event },
                event: Event,
            },
            defaultDate: moment().toDate(),
            formats: {
                dayHeaderFormat: (date: Date) => getExtenseDatetime(date, false),
                timeGutterFormat: (date: Date) => moment(date).format("HH:mm"),
                dayFormat: (date: Date) => moment(date).format("DD"),
                weekdayFormat: (date: Date) => extenseWeekday(moment(date).weekday()),
                eventTimeRangeFormat: () => "",
            },
            messages: {
                date: 'Data',
                time: 'Hora',
                event: 'Evento',
                allDay: 'Dia Todo',
                week: 'Semana',
                work_week: 'Dia Útil',
                day: 'Dia',
                month: 'Mês',
                previous: 'Anterior',
                next: 'Próximo',
                yesterday: 'Ontem',
                tomorrow: 'Amanhã',
                today: 'Hoje',
                agenda: 'Agenda',

                noEventsInRange: 'Nenhum compromisso no período selecionado.',
                showMore: (total, remainingEvents, events) => `+${total} eventos`,
            },
        }),
        []
    );

    const eventPropGetter = useCallback(
        (event, start, end, isSelected) => ({
            style: {
                background: '#F4F4F4',
                border: '1px solid #CACACA',
                color: '#8a8a8a',
                padding: '2px',
                minHeight: 25,
            }
        }),
        []
    )

    useEffect(() => {
        setTasks(
            [
                ...contracts.reduce((pv, contract) => {
                    const items = (contract.cars ?? []).reduce((pv: any[], { start_at, finish_at, car }: any) => pv.concat(
                        [
                            {
                                done: true,
                                title: `${car?.license_plate} - Retirada`,
                                description: `Retirada de Veículo`,
                                subtitle: `${car?.license_plate}`,
                                start: moment(start_at).toDate(),
                                end: moment(start_at).toDate(),
                            },
                            {
                                done: !!finish_at,
                                title: `${car?.license_plate} - Devolução`,
                                description: `Devolução de Veículo`,
                                subtitle: `${car?.license_plate}`,
                                start: moment(finish_at ?? contract.finish_at).toDate(),
                                end: moment(finish_at ?? contract.finish_at).toDate(),
                            },
                        ].filter(nn => nn)
                    ), [])

                    return pv.concat(items);
                }, []),
                ...customers.reduce((pv, customer) => {
                    const items = [
                        {
                            done: customer.status == 2,
                            title: `Validar Identidade - ${customer.name}`,
                            description: `Validar identidade de cliente`,
                            subtitle: `${customer.name}`,
                            start: moment(customer.createdAt).toDate(),
                            end: moment(customer.createdAt).toDate(),
                        }
                    ].filter(nn => nn)

                    return pv.concat(items);
                }, []),
                ...supports.reduce((pv, { car, status, datetime, finish_at, title }: any) => {
                    const items = [
                        {
                            done: status != 1,
                            title: `${car?.license_plate} - ${title} (Iniciar)`,
                            subtitle: `${car?.license_plate}`,
                            description: `Iniciar Manutenção - ${title}`,
                            start: moment(datetime).toDate(),
                            end: moment(datetime).toDate(),
                        },
                        finish_at && {
                            done: [0, 2].includes(status),
                            title: `${car?.license_plate} - ${title} (Finalizar)`,
                            subtitle: `${car?.license_plate}`,
                            description: `Finalizar Manutenção - ${title}`,
                            start: moment(finish_at).toDate(),
                            end: moment(finish_at).toDate(),
                        },
                    ].filter(nn => nn)

                    return pv.concat(items);
                }, []),
            ].sort((a, b) => a.start > b.start ? 1 : -1)
        )
    }, [supports, contracts, customers]);

    useEffect(() => { loadData() }, []);

    return <Paper p="md" shadow="xs">
        <Calendar
            components={components}
            localizer={localizer}
            events={tasks}
            startAccessor="start"
            endAccessor="end"
            eventPropGetter={eventPropGetter}
            formats={formats}
            messages={messages}
            defaultDate={defaultDate}
            defaultView={Views.MONTH}
            onSelectEvent={(item: any) => { }}
            style={{ height: '85vh' }}
        />
    </Paper>
}